<script setup lang="ts">
import Box from '@uiKit/elements/Box.vue'
import AppButton from '@uiKit/elements/Button.vue'
import Text from '@uiKit/elements/Text.vue'
import TokenConnectPlaceholder from './TokenConnectPlaceholder.vue'

const ADDRESS_PREFIX_CHARS = 8
const ADDRESS_SUFFIX_CHARS = 6

const props = defineProps<{
  walletAddress: string
}>()

const emit = defineEmits<{
  (e: 'enable'): void
}>()

function shortAddress(addr = '', prefixLength = ADDRESS_PREFIX_CHARS) {
  if (!addr) return ''
  if (addr.length <= prefixLength + ADDRESS_SUFFIX_CHARS) return addr
  return `${addr.slice(0, prefixLength)}...${addr.slice(-ADDRESS_SUFFIX_CHARS)}`
}
</script>

<template>
  <Box align="start" gap="6" class="twet">
    <TokenConnectPlaceholder
      title="Enable Trustline"
      description="To receive ARVA property tokens, you need to set up a trustline for our token on the XRP Ledger. This is a one-time setup that authorizes your wallet to accept ARVA tokens."
    />

    <Box direction="col" gap="2" grow class="twet__right">
      <Box class="twet__card twet__details">
        <Text type="heading">Trustline Details</Text>
        <Box direction="col" gap="2" class="twet__details-list">
          <Box align="center" justify="between" class="twet__details-row">
            <Text type="body" weight="medium" color="muted" is="span">Token</Text>
            <Text type="body" weight="semibold" is="span">ARVA (Sliceal)</Text>
          </Box>
          <Box align="center" justify="between" class="twet__details-row">
            <Text type="body" weight="medium" color="muted" is="span">Network</Text>
            <Text type="body" weight="semibold" is="span">XRP Ledger Mainnet</Text>
          </Box>
          <Box align="center" justify="between" class="twet__details-row">
            <Text type="body" weight="medium" color="muted" is="span">Your Wallet</Text>
            <Text type="body" weight="semibold" is="span">
              {{ shortAddress(props.walletAddress) }}
            </Text>
          </Box>
        </Box>
      </Box>

      <Box direction="col" gap="3" class="twet__card twet__instructions">
        <Text type="heading">Setup Instructions</Text>
        <Box direction="col" gap="2" class="twet__steps">
          <Box align="start" gap="3" class="twet__step">
            <Text type="body" weight="semibold" align="center" is="div" class="twet__step-num">
              1
            </Text>
            <Text type="body" is="div" class="twet__step-content">
              Tap
              <Text is="strong" type="body" weight="bold" color="default">Send Trustline Request</Text>
              below. If your wallet app is installed, it will open automatically.
            </Text>
          </Box>
          <Box align="start" gap="3" class="twet__step">
            <Text type="body" weight="semibold" align="center" is="div" class="twet__step-num">
              2
            </Text>
            <Text type="body" is="div" class="twet__step-content">
              If the app does not open,
              <Text is="strong" type="body" weight="bold" color="default">
                manually open your XRP wallet app
              </Text>
              (e.g. Xaman / XUMM).
            </Text>
          </Box>
          <Box align="start" gap="3" class="twet__step">
            <Text type="body" weight="semibold" align="center" is="div" class="twet__step-num">
              3
            </Text>
            <Text type="body" is="div" class="twet__step-content">
              Find the pending trustline request and
              <Text is="strong" type="body" weight="bold" color="default">
                approve the transaction
              </Text>.
            </Text>
          </Box>
          <Box align="start" gap="3" class="twet__step">
            <Text type="body" weight="semibold" align="center" is="div" class="twet__step-num">
              4
            </Text>
            <Text type="body" is="div" class="twet__step-content">
              Return here — the platform will verify confirmation on the blockchain automatically.
            </Text>
          </Box>

          <Box align="center" gap="2" class="twet__note">
            <Text is="span" class="twet__note-icon">🔒</Text>
            <Text type="body" is="div">
              A trustline does not give anyone access to your wallet or funds. It only authorises
              the receipt of ARVA tokens.
            </Text>
          </Box>
        </Box>

        <AppButton
          label="Send Trustline Request"
          size="large"
          class="twet__cta"
          @click="emit('enable')"
        />
        <Text type="caption" align="center" is="div" class="twet__footer">
          You will be redirected to your wallet app to approve
        </Text>
      </Box>
    </Box>
  </Box>
</template>

<style scoped>
.twet {
  width: 100%;
}

.twet__card {
  background: var(--p-surface-0);
  border-radius: var(--p-border-radius-lg);
  padding: 18px;
  box-shadow: 0 0 0 1px rgba(10, 13, 18, 0.02);
}

.twet__details-list {
  margin-top: 0.75rem;
}

.twet__details-row {
  padding: 0.75rem;
  border: 1px solid var(--p-surface-200);
  border-radius: 8px;
}

.twet__step {
  border: 1px solid var(--p-surface-200);
  padding: 14px;
  border-radius: 10px;
}

.twet__step-num {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: var(--p-info-50);
  border: 1px solid var(--p-info-200);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--p-info-700);
  flex-shrink: 0;
}

.twet__step-content {
  color: var(--p-text-color);
}

.twet__note {
  background: var(--p-surface-100);
  padding: 0.75rem;
  border-radius: 10px;
  margin-top: 6px;
  color: var(--p-text-color);
}

.twet__cta {
  margin-top: 0.5rem;
  width: 100% !important;
  border-radius: 10px !important;
}

.twet__footer {
  margin-top: 0.5rem;
  color: var(--p-surface-400);
}

@media (max-width: 992px) {
  .twet {
    flex-direction: column;
    gap: 1.25rem;
  }

  .twet__right {
    width: 100%;
  }
}
</style>
